
import { processAPIData } from './processor';
import { TimePeriod } from './types';

// Importa il servizio server-side per uso diretto in SSR
let metalPriceService: any = null;

// Funzione per importare dinamicamente il servizio server-side
async function getMetalPriceService() {
  if (!metalPriceService && typeof window === 'undefined') {
    try {
      metalPriceService = await import('../../server/services/metalPriceService');
    } catch (error) {
      console.error('[MetalPriceAPI] Failed to import metalPriceService:', error);
      throw error;
    }
  }
  return metalPriceService;
}

// Server endpoints - il client ora chiama il server invece dell'API esterna
const SERVER_BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

async function fetchFromServer(endpoint: string): Promise<any> {
  // Se siamo in ambiente server-side (SSR), usa direttamente il servizio
  if (typeof window === 'undefined') {
    try {
      console.log('[MetalPriceAPI] Using direct server service for:', endpoint);
      const service = await getMetalPriceService();
      
      if (endpoint === 'spot') {
        return await service.getSpotPrices();
      } else if (endpoint === 'fixing') {
        return await service.getFixingPrices();
      } else if (endpoint.startsWith('historical/')) {
        const period = endpoint.replace('historical/', '');
        return await service.getHistoricalPrices(period);
      } else {
        throw new Error(`Unknown endpoint: ${endpoint}`);
      }
    } catch (error) {
      console.error('[MetalPriceAPI] Direct service error:', error);
      throw error;
    }
  }

  // Se siamo in ambiente client-side, usa fetch
  const url = `${SERVER_BASE_URL}/api/metal-prices/${endpoint}`;

  try {
    console.log('[MetalPriceAPI] Fetching from server:', url);
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Server request failed with status ${response.status}: ${errorBody}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(`Server error: ${result.error || 'Unknown error'}`);
    }
    
    return result.data;
  } catch (error) {
    console.error('[MetalPriceAPI] Server fetch error:', error);
    throw error;
  }
}

export async function fetchSpotPricesFromAPI() {
  const rawData = await fetchFromServer('spot');
  return processAPIData(rawData, 'spot');
}

export const fetchCurrentPrices = fetchSpotPricesFromAPI;

export async function fetchFixingPricesFromAPI() {
  const rawData = await fetchFromServer('fixing');
  return processAPIData(rawData, 'fixing');
}

const periodMapping: Record<TimePeriod, string> = {
  week: 'weekly',
  month: 'monthly',
  year: 'yearly',
  all: 'all',
};

export async function fetchHistoryFromAPI(period: TimePeriod) {
  const serverPeriod = periodMapping[period] || 'weekly'; // Fallback to 'weekly'
  const rawData = await fetchFromServer(`historical/${serverPeriod}`);
  return processAPIData(rawData, 'storico');
}
